<template>
  <div class="flex flex-col w-full h-full overflow-y-auto gap-3 pb-4">
    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center h-[60vh]">
      <i class="pi pi-spin pi-spinner text-primary" style="font-size: 2rem" />
    </div>

    <template v-else>
      <!-- Owner value intro (dismissible) -->
      <div v-if="showOwnerIntro" class="relative rounded-xl border border-primary-100 bg-primary-50/50 p-3 flex flex-col gap-2">
        <button type="button" class="absolute top-2 left-2 text-gray-400 hover:text-gray-600" @click="dismissOwnerIntro">
          <i class="pi pi-times" style="font-size: 0.7rem" />
        </button>
        <div class="flex items-center gap-2">
          <i class="pi pi-sparkles text-primary" />
          <span class="font-semibold text-sm">סריקת עור AI — איך זה עוזר לעסק?</span>
        </div>
        <ul class="text-xs text-gray-600 flex flex-col gap-1 pe-1">
          <li>• מעקב לפני/אחרי שמוכיח ללקוח/ה שהטיפול עובד — והיא חוזרת</li>
          <li>• ניתוח חכם שממליץ על הטיפולים <span class="font-medium">שלך</span> → יותר הזמנות תור</li>
          <li>• הלקוח/ה יכול/ה לסרוק גם מהבית — שירות פרימיום שמבדל אותך</li>
        </ul>
      </div>

      <!-- Treatment plan (owner-filled, shown to the customer on the shared link) -->
      <div class="rounded-xl border border-primary-100 bg-white p-3 flex flex-col gap-2">
        <div class="flex items-center justify-between">
          <span class="text-sm font-semibold text-gray-800 flex items-center gap-2">
            <i class="pi pi-file-edit text-primary" /> תוכנית טיפול
          </span>
          <button v-if="!editingPlan" type="button" class="text-xs text-primary" @click="startEditPlan">
            {{ treatmentPlan ? 'עריכה' : 'הוספה' }}
          </button>
        </div>
        <p v-if="!editingPlan && treatmentPlan" class="text-sm text-gray-700 whitespace-pre-wrap">{{ treatmentPlan }}</p>
        <p v-else-if="!editingPlan" class="text-xs text-gray-400">
          כתבו את תוכנית הטיפול של הלקוח/ה — היא תוצג ללקוח/ה בקישור ההתקדמות המשותף.
        </p>
        <div v-else class="flex flex-col gap-2">
          <textarea
            v-model="planDraft"
            rows="4"
            maxlength="1500"
            placeholder="למשל: סדרת 6 טיפולי פילינג כל 3 שבועות, שגרת טיפוח יומית..."
            class="w-full text-sm border border-gray-200 rounded-lg p-2 resize-none focus:outline-none focus:border-primary"
          />
          <div class="flex gap-2 justify-end">
            <button type="button" class="text-xs text-gray-500 px-3 py-1" @click="cancelEditPlan">ביטול</button>
            <button
              type="button"
              class="text-xs text-white bg-primary rounded-full px-3 py-1 disabled:opacity-60"
              :disabled="savingPlan"
              @click="saveTreatmentPlan"
            >{{ savingPlan ? 'שומר...' : 'שמירה' }}</button>
          </div>
        </div>
      </div>

      <!-- Share progress with the customer -->
      <div class="rounded-xl border border-gray-200 bg-white p-3 flex flex-col gap-2">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <i class="pi pi-share-alt text-primary" />
            <span class="text-sm font-medium">שיתוף התוכנית עם הלקוח/ה</span>
          </div>
          <button
            type="button"
            role="switch"
            :aria-checked="shareEnabled"
            :disabled="shareLoading"
            class="relative w-11 h-6 rounded-full transition-colors disabled:opacity-50"
            :class="shareEnabled ? 'bg-primary' : 'bg-gray-200'"
            @click="toggleShare"
          >
            <span class="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all" :class="shareEnabled ? 'left-0.5' : 'right-0.5'" />
          </button>
        </div>
        <p v-if="!shareEnabled" class="text-xs text-gray-400">
          הפעילו כדי לקבל קישור שבו הלקוח/ה רואה גרף התקדמות כללי והיסטוריית תמונות — ללא ציונים או ניתוח מפורט.
        </p>
        <div v-else class="flex items-center gap-2">
          <input readonly :value="shareUrl" class="flex-1 min-w-0 text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-gray-50 text-gray-600 truncate" />
          <button type="button" class="text-xs text-white bg-primary rounded-lg px-3 py-1.5 whitespace-nowrap" @click="copyShare">העתקה</button>
        </div>
      </div>

      <!-- Action bar -->
      <div class="flex items-center justify-between gap-2 pt-1">
        <button
          type="button"
          class="flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-white text-sm font-medium"
          @click="openCapture"
        >
          <i class="pi pi-camera text-xs" />
          <span>סריקה חדשה</span>
        </button>

        <button
          v-if="scans.length >= 2"
          type="button"
          class="flex items-center gap-2 px-3 py-2 rounded-full border text-sm"
          :class="compareMode ? 'border-primary text-primary bg-primary-50' : 'border-gray-300 text-gray-600'"
          @click="toggleCompareMode"
        >
          <i class="pi pi-arrows-h text-xs" />
          <span>{{ compareMode ? 'ביטול השוואה' : 'השוואה' }}</span>
        </button>
      </div>

      <p v-if="compareMode" class="text-xs text-gray-500 text-center">
        בחרו שתי סריקות להשוואה ({{ selected.length }}/2)
      </p>

      <!-- Requested image directions (per customer) -->
      <div v-if="!compareMode" class="rounded-xl border border-gray-200 bg-white p-3 flex flex-col gap-2">
        <div class="flex items-center gap-2">
          <i class="pi pi-images text-primary" />
          <span class="text-sm font-semibold text-gray-800">תמונות נדרשות</span>
        </div>
        <p class="text-xs text-gray-500">
          בחרו אילו זוויות תמונה לבקש מהלקוח/ה (בסריקה עצמית ובמרפאה). תמונת חזית היא הבסיס לניתוח.
        </p>
        <div class="flex items-center gap-2 flex-wrap">
          <button
            v-for="dir in allDirections"
            :key="dir"
            type="button"
            :disabled="dir === FRONT_DIRECTION || savingDirections"
            class="px-3 py-1.5 rounded-full text-xs border transition-colors disabled:opacity-100"
            :class="selectedDirections.includes(dir)
              ? 'bg-primary-50 border-primary text-primary'
              : 'bg-white border-gray-300 text-gray-600'"
            @click="toggleDirection(dir)"
          >
            <i v-if="selectedDirections.includes(dir)" class="pi pi-check text-[10px] me-1" />
            {{ directionLabels[dir] }}
          </button>
        </div>
      </div>

      <!-- Photo tracking (per customer) -->
      <div v-if="!compareMode" class="rounded-xl border border-gray-200 bg-white p-3 flex flex-col gap-2">
        <div class="flex items-center gap-2">
          <i class="pi pi-camera text-primary" />
          <span class="text-sm font-semibold text-gray-800">מעקב תמונות</span>
          <span v-if="trackingActive" class="text-[10px] px-1.5 py-0.5 rounded-full bg-green-50 text-green-600">פעיל</span>
        </div>

        <!-- Automation off → point the owner to the automations page -->
        <div
          v-if="!reminderEnabled"
          class="flex items-center justify-between gap-2 p-2.5 bg-surface-50 rounded-lg border border-surface-200"
        >
          <div class="flex flex-col gap-0.5 pe-2">
            <span class="text-xs font-medium">בקשת תמונות מעקב מנוהלת בעמוד האוטומציות</span>
            <span class="text-[11px] text-gray-500">הפעילו את האוטומציה "בקשת תמונת מעקב" כדי לשלוח ללקוחות בקשה אוטומטית לתמונה.</span>
          </div>
          <button type="button" class="text-xs text-primary whitespace-nowrap flex items-center gap-1" @click="goToAutomations">
            הגדרה <i class="pi pi-arrow-left text-[10px]" />
          </button>
        </div>

        <!-- Automation on → per-customer cadence + complete -->
        <template v-else>
          <p class="text-xs text-gray-500">
            בקשת תמונת פנים תישלח אוטומטית ללקוח/ה בתדירות שתבחרו, עד לסיום המעקב.
          </p>
          <div class="flex items-center gap-2">
            <span class="text-sm text-gray-700">כל</span>
            <input
              v-model.number="frequencyDays"
              type="number"
              min="1"
              class="w-16 text-sm text-center border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-primary"
            />
            <span class="text-sm text-gray-700">ימים</span>
          </div>
          <div class="flex gap-2 justify-end pt-1">
            <button
              v-if="trackingActive"
              type="button"
              class="text-xs text-red-600 px-3 py-1.5 rounded-full border border-red-200 disabled:opacity-60"
              :disabled="savingTracking"
              @click="completeTracking"
            >סיום מעקב</button>
            <button
              type="button"
              class="text-xs text-white bg-primary rounded-full px-3 py-1.5 disabled:opacity-60"
              :disabled="savingTracking || !frequencyDays || frequencyDays < 1"
              @click="saveTracking"
            >{{ trackingActive ? 'עדכון תדירות' : 'הפעלת מעקב' }}</button>
          </div>
        </template>
      </div>

      <!-- Progress trend (overview) -->
      <SkinScanTrend v-if="scans.length >= 2 && !compareMode" :scans="scans" @select="openResult" />

      <!-- Scan list -->
      <div v-if="scans.length" class="flex flex-col gap-2.5">
        <div
          v-for="scan in sortedScans"
          :key="scan._id"
          class="flex items-center gap-3 p-3 bg-white border rounded-xl cursor-pointer transition-colors shadow-sm"
          :class="isSelected(scan) ? 'border-primary ring-1 ring-primary' : 'border-gray-200 hover:border-gray-300'"
          @click="onScanClick(scan)"
        >
          <img :src="scan.imageUrl" alt="scan" class="w-14 h-14 rounded-lg object-cover flex-none border border-gray-100" />
          <div class="flex flex-col flex-1 min-w-0 gap-0.5">
            <span class="text-sm font-medium">{{ formatScanDate(scan.createdAt) }}</span>
            <div class="flex items-center gap-2">
              <span class="text-xs text-gray-500">{{ scan.skinType || 'סריקת עור' }}</span>
              <span class="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                {{ scan.source === 'home' ? 'בית' : 'קליניקה' }}
              </span>
            </div>
          </div>
          <!-- Overall score -->
          <div class="flex flex-col items-center flex-none">
            <span class="text-lg font-bold leading-none tabular-nums" :style="{ color: scoreColor(scan.overallScore) }">
              {{ scan.overallScore }}
            </span>
            <span class="text-[9px] text-gray-400">ציון</span>
          </div>
          <i v-if="!compareMode" class="pi pi-chevron-left text-gray-400 text-sm flex-none" />
          <i
            v-else
            :class="isSelected(scan) ? 'pi pi-check-circle text-primary' : 'pi pi-circle text-gray-300'"
            class="text-base flex-none"
          />
        </div>
      </div>

      <!-- Empty state -->
      <div v-else class="flex flex-col items-center justify-center gap-3 pt-12 px-4 text-center">
        <div class="w-16 h-16 rounded-full bg-primary-50 flex items-center justify-center">
          <i class="pi pi-camera text-primary text-2xl" />
        </div>
        <span class="text-sm font-medium text-gray-700">עדיין אין סריקות עור</span>
        <span class="text-xs text-gray-500 max-w-xs">
          בצעו סריקה ראשונה כדי לקבל ניתוח עור מבוסס AI ולעקוב אחרי השינוי לאורך הטיפולים.
        </span>
      </div>

      <!-- Compare CTA -->
      <div
        v-if="compareMode && selected.length === 2"
        class="sticky bottom-2 mt-2"
      >
        <button
          type="button"
          class="w-full py-3 rounded-full bg-primary text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
          :disabled="comparing"
          @click="runCompare"
        >
          <i v-if="comparing" class="pi pi-spin pi-spinner text-sm" />
          <span>{{ comparing ? 'משווה...' : 'הצגת השוואה' }}</span>
        </button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, type PropType } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useDrawer } from '@torim-app/shared-components';
import {
  type ICustomer,
  type ISkinScan,
  AutomationTypeEnum,
  SkinImageDirectionEnum,
  skinImageDirectionLabels,
  skinImageDirectionOrder,
} from '@torim-app/types';
import { SkinScanApi, CommunicationApi } from '@app/api/admin';
import { useAdminStore } from '@app/stores';
import { AdminRouteNames } from '@app/router/route-names';
import { trackEvent } from '@app/composables/posthog';
import { scoreColor, formatScanDate } from './skin-scan/skin-scan.utils';
import SkinScanCapture from './skin-scan/SkinScanCapture.vue';
import SkinScanResult from './skin-scan/SkinScanResult.vue';
import SkinScanCompare from './skin-scan/SkinScanCompare.vue';
import SkinScanTrend from './skin-scan/SkinScanTrend.vue';

const props = defineProps({
  customer: {
    type: Object as PropType<ICustomer>,
    required: true,
  },
});

const toast = useToast();
const router = useRouter();
const { openDrawer, closeDrawer } = useDrawer();
const adminStore = useAdminStore();

const loading = ref(true);
const scans = ref<ISkinScan[]>([]);
// Always show newest first, by the time the scan was taken.
const sortedScans = computed(() =>
  [...scans.value].sort(
    (a, b) => new Date(b.createdAt as string).getTime() - new Date(a.createdAt as string).getTime(),
  ),
);
const compareMode = ref(false);
const selected = ref<ISkinScan[]>([]);
const comparing = ref(false);

const OWNER_INTRO_KEY = 'skinscan_owner_intro_dismissed';
const showOwnerIntro = ref(localStorage.getItem(OWNER_INTRO_KEY) !== '1');
const dismissOwnerIntro = () => {
  showOwnerIntro.value = false;
  try { localStorage.setItem(OWNER_INTRO_KEY, '1'); } catch { /* ignore */ }
};

// Share the customer's progress (general-change graph + images) via a token link.
const shareEnabled = ref(false);
const shareToken = ref<string | undefined>(undefined);
const shareLoading = ref(false);
// The customer hub: upload + their own progress journey (graph/plan shown once sharing
// is enabled). Same page the photo-reminder SMS links to, so the customer has one place.
const shareUrl = computed(() =>
  shareEnabled.value
    ? `${import.meta.env.VITE_BUSINESS_WEBSITE_DOMAIN}/business/${adminStore.getBusinessSiteData.bid}/skin-scan`
    : '',
);

const loadShare = async () => {
  try {
    const info = await SkinScanApi.getShareInfo(customerId());
    shareEnabled.value = info.enabled;
    shareToken.value = info.token;
  } catch (e) {
    console.error(e);
  }
};

const toggleShare = async () => {
  shareLoading.value = true;
  try {
    const info = await SkinScanApi.setShare(customerId(), !shareEnabled.value);
    shareEnabled.value = info.enabled;
    shareToken.value = info.token;
    toast.success(info.enabled ? 'השיתוף הופעל' : 'השיתוף בוטל');
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לעדכן את השיתוף');
  } finally {
    shareLoading.value = false;
  }
};

const copyShare = async () => {
  try {
    await navigator.clipboard.writeText(shareUrl.value);
    toast.success('הקישור הועתק');
  } catch {
    toast.error('לא הצלחנו להעתיק את הקישור');
  }
};

const customerId = () => String(props.customer.id ?? props.customer._id);

// Facial treatment plan (owner-filled; shown to the customer on the shared link).
const treatmentPlan = ref(props.customer.facialTreatmentPlan || '');
const editingPlan = ref(false);
const planDraft = ref('');
const savingPlan = ref(false);

const loadTreatmentPlan = async () => {
  try {
    const { treatmentPlan: plan } = await SkinScanApi.getTreatmentPlan(customerId());
    treatmentPlan.value = plan || '';
  } catch (e) {
    console.error(e);
  }
};

const startEditPlan = () => {
  planDraft.value = treatmentPlan.value;
  editingPlan.value = true;
};
const cancelEditPlan = () => {
  editingPlan.value = false;
};
const saveTreatmentPlan = async () => {
  savingPlan.value = true;
  try {
    const { treatmentPlan: plan } = await SkinScanApi.setTreatmentPlan(customerId(), planDraft.value.trim());
    treatmentPlan.value = plan || '';
    editingPlan.value = false;
    toast.success('תוכנית הטיפול נשמרה');
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לשמור את תוכנית הטיפול');
  } finally {
    savingPlan.value = false;
  }
};

// Per-customer requested image directions (front locked on — it drives the analysis).
const FRONT_DIRECTION = SkinImageDirectionEnum.Front;
const allDirections = skinImageDirectionOrder;
const directionLabels = skinImageDirectionLabels;
const selectedDirections = ref<SkinImageDirectionEnum[]>([FRONT_DIRECTION]);
const savingDirections = ref(false);

const loadDirections = async () => {
  try {
    const { directions } = await SkinScanApi.getRequestedDirections(customerId());
    if (directions?.length) selectedDirections.value = skinImageDirectionOrder.filter(d => directions.includes(d));
  } catch (e) {
    console.error(e);
  }
};

const toggleDirection = async (dir: SkinImageDirectionEnum) => {
  if (dir === FRONT_DIRECTION || savingDirections.value) return;
  const next = selectedDirections.value.includes(dir)
    ? selectedDirections.value.filter(d => d !== dir)
    : [...selectedDirections.value, dir];
  const ordered = skinImageDirectionOrder.filter(d => next.includes(d) || d === FRONT_DIRECTION);
  const previous = selectedDirections.value;
  selectedDirections.value = ordered;
  savingDirections.value = true;
  try {
    const res = await SkinScanApi.setRequestedDirections(customerId(), ordered);
    selectedDirections.value = skinImageDirectionOrder.filter(d => res.directions.includes(d));
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לשמור את התמונות הנדרשות');
    selectedDirections.value = previous;
  } finally {
    savingDirections.value = false;
  }
};

// Per-customer photo tracking + the business-level reminder automation it depends on.
const DEFAULT_FREQUENCY_DAYS = 14;
const reminderEnabled = ref(false);
const trackingActive = ref(false);
const frequencyDays = ref<number>(DEFAULT_FREQUENCY_DAYS);
const savingTracking = ref(false);

const goToAutomations = () => {
  router.push({ name: AdminRouteNames.Communication, query: { tab: '0' } });
};

const loadTracking = async () => {
  try {
    const configs = await CommunicationApi.fetchAutomationConfigs();
    const tracking = await SkinScanApi.getTracking(customerId());
    const reminderConfig = configs.find(c => c.type === AutomationTypeEnum.SkinScanReminder);
    reminderEnabled.value = !!reminderConfig?.enabled;
    const businessDefault = Number(reminderConfig?.settings?.frequencyDays) || DEFAULT_FREQUENCY_DAYS;
    trackingActive.value = tracking.active;
    frequencyDays.value = tracking.frequencyDays ?? businessDefault;
  } catch (e) {
    console.error(e);
  }
};

const saveTracking = async () => {
  savingTracking.value = true;
  try {
    const res = await SkinScanApi.setTracking(customerId(), true, frequencyDays.value);
    trackingActive.value = res.active;
    if (res.frequencyDays != null) frequencyDays.value = res.frequencyDays;
    toast.success('מעקב התמונות הופעל');
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לעדכן את המעקב');
  } finally {
    savingTracking.value = false;
  }
};

const completeTracking = async () => {
  savingTracking.value = true;
  try {
    const res = await SkinScanApi.setTracking(customerId(), false);
    trackingActive.value = res.active;
    toast.success('המעקב הושלם — לא יישלחו בקשות נוספות');
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לעדכן את המעקב');
  } finally {
    savingTracking.value = false;
  }
};

const loadScans = async () => {
  loading.value = true;
  try {
    scans.value = await SkinScanApi.fetchScans(customerId());
    // Share state is only surfaced once there's something to share.
    if (scans.value.length) await loadShare();
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לטעון את הסריקות');
  } finally {
    loading.value = false;
  }
};

const isSelected = (scan: ISkinScan) => selected.value.some(s => s._id === scan._id);

const toggleCompareMode = () => {
  compareMode.value = !compareMode.value;
  selected.value = [];
};

const onScanClick = (scan: ISkinScan) => {
  if (compareMode.value) {
    if (isSelected(scan)) {
      selected.value = selected.value.filter(s => s._id !== scan._id);
    } else if (selected.value.length < 2) {
      selected.value.push(scan);
    } else {
      // replace the oldest selection
      selected.value = [selected.value[1], scan];
    }
    return;
  }
  openResult(scan);
};

const openResult = (scan: ISkinScan) => {
  trackEvent('skin_scan_result_viewed');
  openDrawer(SkinScanResult, {
    scan,
    onDeleted: () => loadScans(),
    onUpdated: () => loadScans(),
    onClose: closeDrawer,
  }, {
    title: 'תוצאות סריקה',
    closable: true,
    noPadding: true,
  });
};

const openCapture = () => {
  trackEvent('skin_scan_capture_opened');
  openDrawer(SkinScanCapture, {
    customerId: customerId(),
    treatmentSessionId: props.customer.currentTreatmentSession?.id,
    // The capture drawer now shows the result inline; we just refresh the list.
    onScanned: () => loadScans(),
    onChanged: () => loadScans(),
    onClose: closeDrawer,
  }, {
    title: 'סריקת עור',
    closable: true,
    noPadding: true,
  });
};

const runCompare = async () => {
  if (selected.value.length !== 2) return;
  comparing.value = true;
  try {
    const comparison = await SkinScanApi.compareScans(
      String(selected.value[0]._id),
      String(selected.value[1]._id),
    );
    trackEvent('skin_scan_compared');
    openDrawer(SkinScanCompare, {
      comparison,
      onClose: closeDrawer,
    }, {
      title: 'השוואת סריקות',
      closable: true,
      noPadding: true,
    });
    compareMode.value = false;
    selected.value = [];
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו להשוות את הסריקות');
  } finally {
    comparing.value = false;
  }
};

onMounted(() => {
  trackEvent('skin_scan_tab_viewed');
  loadScans();
  loadTreatmentPlan();
  loadTracking();
  loadDirections();
});
</script>
